package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.Event
import com.rainbowclubs.rainbow.domain.use_cases.events.GetClubEventsUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

class EditEventViewModel(
    private val clubId: String,
    private val getClubEventsUseCase: GetClubEventsUseCase,
) : ViewModel() {

    data class UiState(
        val events: List<Event> = emptyList(),
        val nextPage: Int = 1,
        val hasNextPage: Boolean = false,
        val isLoadingMore: Boolean = false,
        val loadMoreError: Boolean = false,
    )

    sealed class NavigationEvent {
        object Back : NavigationEvent()

        data class UpdateEvent(
            val clubId: String,
            val eventId: String,
        ) : NavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<UiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<UiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchEvents()
    }

    fun fetchEvents() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getClubEventsUseCase(
                clubId = clubId,
                page = 1,
                limit = PAGE_SIZE,
            ).onSuccess { response ->
                _screenState.value = ScreenState.Content(
                    UiState(
                        events = response.events,
                        nextPage = response.page + 1,
                        hasNextPage = response.hasNextPage,
                    )
                )
            }.onFailure {
                _screenState.value = ScreenState.Error(it)
            }
        }
    }

    fun loadNextPage() {
        val currentState = (_screenState.value as? ScreenState.Content)?.data ?: return
        if (currentState.isLoadingMore || !currentState.hasNextPage) return

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(
                currentState.copy(
                    isLoadingMore = true,
                    loadMoreError = false,
                )
            )

            getClubEventsUseCase(
                clubId = clubId,
                page = currentState.nextPage,
                limit = PAGE_SIZE,
            ).onSuccess { response ->
                val updatedState =
                    (_screenState.value as? ScreenState.Content)?.data ?: currentState
                _screenState.value = ScreenState.Content(
                    updatedState.copy(
                        events = updatedState.events + response.events,
                        nextPage = response.page + 1,
                        hasNextPage = response.hasNextPage,
                        isLoadingMore = false,
                        loadMoreError = false,
                    )
                )
            }.onFailure {
                val updatedState =
                    (_screenState.value as? ScreenState.Content)?.data ?: currentState
                _screenState.value = ScreenState.Content(
                    updatedState.copy(
                        isLoadingMore = false,
                        loadMoreError = true,
                    )
                )
            }
        }
    }

    fun selectEvent(event: Event) {
        val eventId = event.id ?: return
        navigationIntentChannel.trySend(
            NavigationEvent.UpdateEvent(
                clubId = clubId,
                eventId = eventId,
            )
        )
    }

    private companion object {
        const val PAGE_SIZE = 20
    }
}
